/*
 * What a new Housecall Pro customer is made from.
 *
 * One function, used twice: the API sends what it returns, and the client
 * page shows the same thing in the confirm before anything is created. A
 * person agreeing to "create this customer" should be agreeing to exactly
 * what HCP will receive.
 */

import type { ContactDto } from '../dto/contact.dto.js'
import type { SiteDto } from '../dto/site.dto.js'
import type { ClientType } from '../enums/client-type.enum.js'

/** An address as HCP's create endpoints take it: all but the second line required. */
export interface HcpAddressDraft {
  street: string
  streetLine2: string | null
  city: string
  state: string
  zip: string
}

/** The address fields a site has, under the names a site has them. */
export type SiteAddressFields = Pick<
  SiteDto,
  'addressLine1' | 'addressLine2' | 'city' | 'state' | 'postalCode'
>

/** A site, as much of it as a draft reads. */
type DraftSite = Pick<SiteDto, 'id' | 'name' | 'archivedAt'> & SiteAddressFields

/** A contact, as much of it as a draft reads. Archived is an ISO instant. */
type DraftContact = Pick<
  ContactDto,
  'name' | 'email' | 'phone' | 'isPrimary' | 'archivedAt'
>

/**
 * The person a preview and a create payload both name.
 *
 * HCP requires one of a name, an email, or a phone number. The company alone
 * is not enough. For an association the company is the client's name; a
 * household has none. On a customer HCP already has, the company is theirs,
 * not this business's.
 */
export interface HcpPerson {
  firstName: string | null
  lastName: string | null
  company: string | null
  email: string | null
  mobileNumber: string | null
}

/** The customer a client would become in HCP. */
export interface HcpCustomerDraft extends HcpPerson {
  /** One per site with a complete address, each to be linked to its site. */
  addresses: { siteId: string; siteName: string; address: HcpAddressDraft }[]
  /** Sites left out because their address is incomplete. */
  sitesWithoutAddress: string[]
}

/**
 * What creating a customer sends. The addresses are HCP's, without the site
 * link the preview keeps so each one can be stored against its site.
 */
export interface HcpCustomerCreate extends HcpPerson {
  addresses: HcpAddressDraft[]
}

/** Text with something in it, trimmed; otherwise null. */
function filled(value: string | null): string | null {
  const trimmed = value?.trim() ?? ''
  return trimmed === '' ? null : trimmed
}

/**
 * A site's address as HCP takes one, or null when a part HCP needs is
 * missing: HCP refuses an address without a street, city, state and ZIP.
 */
export function hcpAddressOfSite(site: SiteAddressFields): HcpAddressDraft | null {
  const street = filled(site.addressLine1)
  const city = filled(site.city)
  const state = filled(site.state)
  const zip = filled(site.postalCode)
  if (street === null || city === null || state === null || zip === null) {
    return null
  }
  return { street, streetLine2: filled(site.addressLine2), city, state, zip }
}

/**
 * "Sam Rivera" as first and last name: the last word is the last name, which
 * is how HCP shows a customer. One word is a first name alone.
 */
export function splitPersonName(name: string): {
  firstName: string | null
  lastName: string | null
} {
  const words = name.trim().split(/\s+/).filter((word) => word !== '')
  if (words.length === 0) return { firstName: null, lastName: null }
  if (words.length === 1) return { firstName: words[0] ?? null, lastName: null }
  return {
    firstName: words.slice(0, -1).join(' '),
    lastName: words[words.length - 1] ?? null,
  }
}

/**
 * The customer to create for a client, or null when it has no primary
 * contact HCP can name. HCP needs a person's name, an email, or a phone
 * number, and the primary contact is who the business deals with. A primary
 * contact with none of those is the same as none: the company name is not
 * enough, and sending the blank contact makes HCP refuse the create.
 *
 * Archived contacts and sites are left out: they are not who or where the
 * work is.
 */
export function hcpCustomerDraft(
  client: { name: string; type: ClientType },
  contacts: readonly DraftContact[],
  sites: readonly DraftSite[],
): HcpCustomerDraft | null {
  const primary =
    contacts.find((contact) => contact.isPrimary && contact.archivedAt === null) ??
    null
  if (primary === null) return null
  const person = splitPersonName(primary.name)
  const email = filled(primary.email)
  const mobileNumber = filled(primary.phone)
  if (person.firstName === null && email === null && mobileNumber === null) {
    return null
  }

  const addresses: HcpCustomerDraft['addresses'] = []
  const sitesWithoutAddress: string[] = []
  for (const site of sites) {
    if (site.archivedAt !== null) continue
    const address = hcpAddressOfSite(site)
    if (address === null) {
      sitesWithoutAddress.push(site.name)
    } else {
      addresses.push({ siteId: site.id, siteName: site.name, address })
    }
  }

  return {
    ...person,
    company: client.type === 'COMMERCIAL' ? client.name : null,
    email,
    mobileNumber,
    addresses,
    sitesWithoutAddress,
  }
}
